import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Radio,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MicIcon from '@mui/icons-material/Mic';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StopIcon from '@mui/icons-material/Stop';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import CustomerAutocomplete from '../shared/CustomerAutocomplete';
import DecimalField from '../shared/DecimalField';
import VehicleRegistrationField, { EMPTY_VEHICLE } from '../vehicles/VehicleRegistrationField';
import { calcPriceInclVat, formatPrice, formatPriceWithCurrency } from '../../utils/vat';
import { codeFont, numFont } from '../../utils/fonts';
import { formatQuantity } from '../../utils';
import useAudioRecorder, { MAX_RECORDING_SECONDS } from './useAudioRecorder';
import { isQuoteMachineLaunchPeriod } from './launch';

const launchHiddenKey = (orgId) => `quote_machine_launch_hidden_${orgId || 'none'}`;

function readLaunchHidden(orgId) {
  try {
    return localStorage.getItem(launchHiddenKey(orgId)) === '1';
  } catch (e) {
    return false;
  }
}

const QUICK_COUNT = 12;
// Confidence tags: saturated text on a pale tint, as StatusBadge pairs its status colours.
const TIER_STYLE = {
  good: { color: '#2f8c54', bgcolor: '#e6ffed', borderColor: '#9ae6b4', Icon: VerifiedOutlinedIcon },
  fair: { color: '#b45309', bgcolor: '#fef3e2', borderColor: '#fbd38d', Icon: InsightsOutlinedIcon },
  rough: { color: '#c05621', bgcolor: '#fffaf0', borderColor: '#fbd38d', Icon: InsightsOutlinedIcon },
  none: { color: '#64748b', bgcolor: '#f8fafc', borderColor: '#e2e8f0', Icon: SearchIcon },
};
// A segmented control (plate / make and model, yes / no): the group is the track, the chosen
// button a white pill on it.
const SEGMENT_SX = {
  bgcolor: 'background.default',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '9px',
  p: '3px',
  gap: '2px',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '7px !important',
    px: 1.5,
    py: 0.5,
    lineHeight: 1.4,
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    '&.Mui-selected': {
      bgcolor: 'background.paper',
      color: 'primary.main',
      boxShadow: '0 1px 2px rgba(0,0,0,.08)',
      '&:hover': { bgcolor: 'background.paper' },
    },
  },
};
// The vehicle fields the fitting knowledge base keys on (composition vehicle_key.build_key); the
// registration lookup result carries them all, an older vehicle row some of them.
const VEHICLE_KEY_FIELDS = [
  'tecdoc_id', 'engine_code', 'drive_type', 'body_type', 'fuel_type', 'fuel', 'model_year_from',
  'model_manufactured_from',
];
const AUDIO_EXTENSION = { 'audio/webm': 'webm', 'audio/mp4': 'mp4', 'audio/ogg': 'ogg', 'audio/wav': 'wav' };

const hasDiscount = (customer) => ['discount_percent', 'jobs_discount_percent', 'parts_discount_percent']
  .some((field) => parseFloat(customer?.[field]) > 0);

/** The answers a freshly picked template starts with: the workshop's usual ones, or by year. */
const initialAnswers = (template, vehicleYear) => {
  const answers = {};
  template.questions.forEach((question) => {
    const byYear = question.default_from_year;
    answers[question.id] = byYear && vehicleYear && vehicleYear >= byYear.year ? byYear.answer : question.default;
  });
  return answers;
};

// A job done per axle (brake pads, calipers, shocks ...) can be on the quote once per axle: the
// pick's identity is the template and the axle, as the server keys it (catalogue.pick_id).
const AXLE = 'axle';
const AXLE_BOTH = 'both';
const hasAxle = (template) => !!template?.questions?.some((question) => question.id === AXLE);
const pickKey = (template, answers) => (hasAxle(template) ? `${template.slug}:${answers?.[AXLE] || 'front'}` : template.slug);
// Element ids of a pick's own rows (prices, parts): the template, with the axle when it has one.
const pickTestId = (template, answers) => (hasAxle(template) ? `${template.slug}-${answers?.[AXLE] || 'front'}` : template.slug);
const sameAnswers = (a, b, questions) => (questions || []).every((question) => (a || {})[question] === (b || {})[question]);

/**
 * Tarjousautomaatti: a quick, indicative quote priced from the workshop's own invoices and,
 * where it has none, from what similar workshops charge.
 *
 * A separate window on purpose. It shares no state with the "new work order", "new quote" or
 * AI work order dialogs; what it creates is an ordinary work order in the quote state.
 */
function QuoteMachineDialog({ open, handleClose, addCreatedMaintenance }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const [launchHidden, setLaunchHidden] = useState(() => readLaunchHidden(currentOrganization?.organization_id));
  const hideLaunchInfo = () => {
    try {
      localStorage.setItem(launchHiddenKey(currentOrganization?.organization_id), '1');
    } catch (e) {
      // Private mode or blocked storage: the box simply comes back next time.
    }
    setLaunchHidden(true);
  };
  const canEditSettings = can(currentOrganization, 'settings.features');

  const [meta, setMeta] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [vehicleMode, setVehicleMode] = useState('plate');
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  const [typed, setTyped] = useState({ make: null, model: null, litres: '' });
  const [customer, setCustomer] = useState(null);
  const [picks, setPicks] = useState([]);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pricing, setPricing] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [interpreting, setInterpreting] = useState(false);
  const [interpretError, setInterpretError] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  // Names of the jobs the last interpretation added, shown under the text field.
  const [added, setAdded] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  // Parts that probably fit, per picked job (keyed by the pick: template, with the axle):
  // {jobs: {pick: {fit, main, extras, parts, estimate_net}}, can_probe, probed}
  const [fitting, setFitting] = useState(null);
  const [probing, setProbing] = useState(false);
  // Which jobs the pool has seen on this vehicle's model: {vehicle: {known}, jobs: {slug: {n_model,
  // hidden_reason}}}. Null = unknown vehicle or no answer yet: nothing is filtered.
  const [vehicleJobs, setVehicleJobs] = useState(null);
  const [showUnknown, setShowUnknown] = useState(false);
  const vehicleJobsRequestRef = useRef(0);

  const sessionRef = useRef(null);
  // Companion jobs (usually on the same visit) the user removed in this window: not re-added.
  const dismissedUsuallyRef = useRef(new Set());
  const fittingRequestRef = useRef(0);
  // Picks whose real-part switch the user has set by hand: the auto choice leaves them alone.
  const partTouchedRef = useRef(new Set());
  const openedAtRef = useRef(null);
  // True while the customer came from the plate lookup (the vehicle's last customer): another
  // plate replaces it, and editing the plate drops it. A customer picked by hand stays.
  const autoCustomerRef = useRef(false);
  const requestRef = useRef(0);

  const templatesBySlug = useMemo(() => {
    const map = {};
    (meta?.templates || []).forEach((template) => { map[template.slug] = template; });
    return map;
  }, [meta]);

  // ---------------------------------------------------------------- open / reset
  useEffect(() => {
    if (!open) return undefined;
    sessionRef.current = null;
    dismissedUsuallyRef.current = new Set();
    openedAtRef.current = new Date().toISOString();
    requestRef.current += 1;
    setVehicleMode('plate');
    setVehicle(EMPTY_VEHICLE);
    setTyped({ make: null, model: null, litres: '' });
    setCustomer(null);
    setPicks([]);
    setSearch('');
    setShowAll(false);
    setQuote(null);
    setPriceError(null);
    setFreeText('');
    setInterpretError(null);
    setUnmatched([]);
    setAdded([]);
    setCreateError(null);
    setLoadError(null);
    setFitting(null);
    setVehicleJobs(null);
    setShowUnknown(false);
    partTouchedRef.current = new Set();
    autoCustomerRef.current = false;
    let cancelled = false;
    axios
      .get('/api/quote-machine/templates/', { params: { language: i18n.language } })
      .then((resp) => { if (!cancelled) setMeta(resp.data); })
      .catch((error) => {
        if (!cancelled) setLoadError(error.response?.data?.error || t('quoteMachine.errors.load'));
      });
    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- vehicle
  const vehiclePayload = useMemo(() => {
    if (vehicleMode === 'model') {
      const litres = parseFloat(typed.litres);
      return {
        manufacturer: typed.make || '',
        model: typed.model || '',
        displacement_cc: litres > 0 ? Math.round(litres * 1000) : null,
      };
    }
    const payload = {
      id: vehicle.id || null,
      registration_number: vehicle.registration_number || '',
      manufacturer: vehicle.manufacturer || '',
      model: vehicle.model || '',
      model_designation: vehicle.model_designation || '',
      displacement_cc: vehicle.displacement_cc || null,
      year_model: vehicle.year_model || null,
    };
    VEHICLE_KEY_FIELDS.forEach((field) => {
      if (vehicle[field] !== undefined && vehicle[field] !== null && vehicle[field] !== '') payload[field] = vehicle[field];
    });
    return payload;
  }, [vehicleMode, vehicle, typed]);
  const vehicleYear = vehicleMode === 'plate' ? parseInt(vehicle.year_model, 10) || null : null;
  const hasPlate = vehicleMode === 'plate' && (vehicle.registration_number || '').trim().length > 0;
  // Make and model are known: from the registry lookup or a vehicle on file (plate mode), or
  // from the make and model picker. Nothing is priced without them, and the server refuses too:
  // a quote on the job type alone was how 22 % of real quotes went out before 2026-10-28.
  const vehicleKnown = vehicleMode === 'plate'
    ? !!((vehicle.manufacturer || '').trim() && (vehicle.model || '').trim())
    : !!(typed.make && typed.model);

  // ---------------------------------------------------------------- picks
  const keyOf = (pick) => pickKey(templatesBySlug[pick.template], pick.answers);
  const picksOf = (slug) => picks.filter((pick) => pick.template === slug);

  const addPick = useCallback((slug, answers, source = 'menu', withCompanions = true, usuallyShare = null) => {
    const template = templatesBySlug[slug];
    if (!template) return;
    const merged = { ...initialAnswers(template, vehicleYear), ...(answers || {}) };
    const key = pickKey(template, merged);
    if (picks.some((pick) => keyOf(pick) === key)) return;
    setPicks((current) => {
      if (current.some((pick) => pickKey(templatesBySlug[pick.template], pick.answers) === key)) return current;
      return [...current, {
        template: slug,
        answers: merged,
        use_estimate: false,
        source,
        // A price the user typed for this line; null until they touch the field.
        labour: null,
        parts: null,
        // Real parts chosen from the fitting offer (the main part first, then extras); empty
        // keeps the estimate line.
        parts_chosen: [],
        // Share of the picked job's visits this job was on, when it came along as a companion.
        usually_share: usuallyShare,
      }];
    });
    // An answer that means "the other job too" (discs with pads) brings that job along, whether
    // the user clicked it or it is the workshop's usual answer: the price is for both jobs. The
    // companion takes the picker's carried answers (the axle: front pads bring front discs).
    if (withCompanions) {
      Object.entries(merged).forEach(([questionId, value]) => {
        const companion = template.companions?.[`${questionId}:${value}`];
        if (!companion) return;
        const carried = Object.fromEntries((companion.carry || []).map((id) => [id, merged[id]]));
        addPick(companion.template, { ...companion.answers, ...carried }, source, false);
      });
      // Jobs usually done on the same visit (the pool's co-occurrence table, served per
      // template): the frequent ones come along as cards of their own, which the user can
      // remove; a removed one stays out for the rest of this window. A companion never brings
      // its own companions.
      (template.usually_with || []).forEach((other) => {
        if (!other.preselect || dismissedUsuallyRef.current.has(other.template)) return;
        addPick(other.template, {}, 'usually', false, other.share);
      });
    }
  }, [templatesBySlug, vehicleYear, picks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropping a job also withdraws the answer on another job that meant "together with it"
  // (discs with pads on the same axle): that job is priced on its own again, so its typed
  // prices and parts go.
  const removePicks = (keys) => setPicks((current) => {
    const dropped = current.filter((pick) => keys.includes(pickKey(templatesBySlug[pick.template], pick.answers)));
    return current
      .filter((pick) => !dropped.includes(pick))
      .map((pick) => {
        const template = templatesBySlug[pick.template];
        let answers = pick.answers;
        Object.entries(template?.companions || {}).forEach(([key, other]) => {
          const [questionId, value] = key.split(':');
          if (pick.answers[questionId] !== value) return;
          const gone = dropped.some((d) => d.template === other.template && sameAnswers(d.answers, pick.answers, other.carry));
          if (!gone) return;
          const question = template.questions.find((item) => item.id === questionId);
          const fallback = question?.answers.find((option) => option.id !== value);
          if (fallback) answers = { ...answers, [questionId]: fallback.id };
        });
        if (answers === pick.answers) return pick;
        partTouchedRef.current.delete(pickKey(template, pick.answers));
        return { ...pick, answers, labour: null, parts: null, parts_chosen: [] };
      });
  });
  // The card's close button and the menu chip: the job goes, on every axle it was on.
  const removeTemplate = (slug) => {
    dismissedUsuallyRef.current.add(slug);
    removePicks(picksOf(slug).map(keyOf));
  };

  const togglePick = (slug) => {
    if (picksOf(slug).length) removeTemplate(slug);
    else addPick(slug);
  };

  const updatePick = (key, changes) => setPicks((current) => current.map(
    (pick) => (pickKey(templatesBySlug[pick.template], pick.answers) === key ? { ...pick, ...changes } : pick)
  ));

  // A scope answer is asked once per card and holds for the job on every axle it is on.
  const answer = (slug, questionId, value) => {
    if (value === null || questionId === AXLE) return;
    const template = templatesBySlug[slug];
    const own = picksOf(slug);
    if (!template || !own.length) return;
    // A changed scope is a different job: a price typed for the old one no longer applies. A
    // chosen part survives; the fitting is asked again, which refreshes the offer and its flags.
    own.forEach((pick) => updatePick(keyOf(pick), {
      answers: { ...pick.answers, [questionId]: value },
      labour: null,
      parts: null,
    }));
    const companions = template.companions || {};
    // The answer the user moved away from may have brought a companion job ("discs too" brought
    // the discs job): it goes with the answer, on the same axle. Otherwise the two jobs on the
    // quote together would force the answer straight back and "no" could never stick.
    const gone = [];
    Object.entries(companions).forEach(([key, other]) => {
      if (!key.startsWith(`${questionId}:`) || key === `${questionId}:${value}`) return;
      own.forEach((pick) => {
        const target = templatesBySlug[other.template];
        const carried = Object.fromEntries((other.carry || []).map((id) => [id, pick.answers[id]]));
        if (target) gone.push(pickKey(target, { ...other.answers, ...carried }));
      });
    });
    if (gone.length) removePicks(gone);
    const companion = companions[`${questionId}:${value}`];
    if (companion) {
      own.forEach((pick) => {
        const carried = Object.fromEntries((companion.carry || []).map((id) => [id, pick.answers[id]]));
        addPick(companion.template, { ...companion.answers, ...carried });
      });
    }
  };

  // The axle control of a card: front, rear or both. "Both" is the job twice on the quote, once
  // per axle, each priced and fitted on its own. Moving a single job to the other axle takes
  // the job it is done together with along (front pads with their discs become rear pads with
  // rear discs); narrowing from both axles to one leaves the other card as it is, priced on
  // its own again.
  const setAxle = (slug, mode) => {
    if (mode === null) return;
    const template = templatesBySlug[slug];
    const own = picksOf(slug);
    if (!template || !own.length) return;
    const wanted = mode === AXLE_BOTH ? ['front', 'rear'] : [mode];
    const base = own[0].answers;
    const leaving = own.filter((pick) => !wanted.includes(pick.answers[AXLE]));
    const gone = leaving.map(keyOf);
    const moving = own.length === 1 && leaving.length === 1 && wanted.length === 1;
    if (moving) {
      // The together partner on the old axle goes too; the new axle brings it back as a companion.
      (meta?.together || []).forEach((rule) => {
        const [partner, partnerAnswers, mine] = rule.a === slug
          ? [rule.b, rule.b_answers, rule.a_answers] : rule.b === slug ? [rule.a, rule.a_answers, rule.b_answers] : [];
        if (!partner) return;
        const pick = leaving[0];
        if (!Object.entries(mine).every(([q, v]) => pick.answers[q] === v)) return;
        picksOf(partner).forEach((other) => {
          if (!sameAnswers(other.answers, pick.answers, rule.match)) return;
          if (!Object.entries(partnerAnswers).every(([q, v]) => other.answers[q] === v)) return;
          gone.push(keyOf(other));
        });
      });
    }
    if (gone.length) removePicks(gone);
    wanted.forEach((axle) => {
      if (own.some((pick) => pick.answers[AXLE] === axle)) return;
      addPick(slug, { ...base, [AXLE]: axle }, own[0].source);
    });
  };

  const mergeInterpreted = (result, source) => {
    setAdded((result.picks || []).map((pick) => templatesBySlug[pick.template]?.name).filter(Boolean));
    // A scope the speaker stated ("levyineen") brings its companion job, as a click would.
    (result.picks || []).forEach((pick) => addPick(pick.template, pick.answers, source));
    setUnmatched(result.unmatched || []);
    if (!(result.picks || []).length && !(result.unmatched || []).length) {
      setInterpretError(t('quoteMachine.noJobsFound'));
    }
  };

  // Jobs that are one visit when both are on the quote on the same axle (pads and discs): their
  // answers must say so, or the set-up labour is priced twice. The server forces the same
  // answers when pricing; this keeps the toggles honest about what is priced.
  useEffect(() => {
    (meta?.together || []).forEach((rule) => {
      [[rule.a, rule.a_answers, rule.b], [rule.b, rule.b_answers, rule.a]].forEach(([slug, forced, partner]) => {
        picks.filter((item) => item.template === slug).forEach((pick) => {
          const other = picks.some((item) => item.template === partner && sameAnswers(item.answers, pick.answers, rule.match));
          if (!other) return;
          const wrong = Object.entries(forced).some(([question, value]) => pick.answers[question] !== value);
          if (wrong) updatePick(keyOf(pick), { answers: { ...pick.answers, ...forced } });
        });
      });
    });
  }, [picks, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- price
  const picksKey = JSON.stringify(picks);
  const vehicleKey = JSON.stringify(vehiclePayload);
  useEffect(() => {
    if (!open || !meta) return undefined;
    if (picks.length === 0 || !vehicleKnown) {
      requestRef.current += 1;
      setQuote(null);
      setPricing(false);
      return undefined;
    }
    requestRef.current += 1;
    const id = requestRef.current;
    setPricing(true);
    const timer = setTimeout(async () => {
      const overrides = {};
      picks.forEach((pick, index) => {
        const edited = ['labour', 'parts'].filter((field) => pick[field] !== null && pick[field] !== '');
        if (edited.length) {
          overrides[index] = Object.fromEntries(edited.map((field) => [field, pick[field]]));
        }
      });
      try {
        const resp = await axios.post('/api/quote-machine/price/', {
          session_id: sessionRef.current,
          opened_at: openedAtRef.current,
          language: i18n.language,
          vehicle: vehiclePayload,
          jobs: picks.map(({ template, answers, use_estimate, source, parts_chosen }) => ({
            template, answers, use_estimate, source, parts_chosen,
          })),
          overrides,
        });
        if (id !== requestRef.current) return;
        sessionRef.current = resp.data.session_id;
        setQuote(resp.data);
        setPriceError(null);
      } catch (error) {
        if (id !== requestRef.current) return;
        setPriceError(error.response?.data?.error || t('quoteMachine.errors.price'));
      } finally {
        if (id === requestRef.current) setPricing(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, meta, picksKey, vehicleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- fitting (real parts)
  // Parts that probably fit, asked once per change of vehicle or of the jobs and their answers
  // (not on every typed price). A different vehicle drops every chosen part: it was chosen for
  // the old one.
  const fittingKey = JSON.stringify(picks.map((pick) => [pick.template, pick.answers]));
  const fetchFitting = useCallback(async (probe) => {
    fittingRequestRef.current += 1;
    const id = fittingRequestRef.current;
    if (probe) setProbing(true);
    try {
      const resp = await axios.post('/api/quote-machine/parts/', {
        vehicle: vehiclePayload,
        jobs: picks.map(({ template, answers }) => ({ template, answers })),
        probe: !!probe,
        session_id: sessionRef.current,
      });
      if (id !== fittingRequestRef.current) return;
      setFitting(resp.data);
    } catch (error) {
      if (id !== fittingRequestRef.current) return;
      if (!probe) setFitting(null); // no suggestions is a quiet state, the estimate line stays
    } finally {
      if (id === fittingRequestRef.current) setProbing(false);
    }
  }, [vehiclePayload, picks]);

  useEffect(() => {
    partTouchedRef.current = new Set();
    setPicks((current) => (
      current.some((pick) => pick.parts_chosen?.length)
        ? current.map((pick) => ({ ...pick, parts_chosen: [] }))
        : current
    ));
  }, [vehicleKey]);

  useEffect(() => {
    if (!open || !meta) return undefined;
    if (picks.length === 0 || !(hasPlate || vehicleMode === 'model')) {
      fittingRequestRef.current += 1;
      setFitting(null);
      return undefined;
    }
    const timer = setTimeout(() => fetchFitting(false), 400);
    return () => clearTimeout(timer);
  }, [open, meta, fittingKey, vehicleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- jobs the model has had
  // Asked once per vehicle; an error or an unknown vehicle leaves the menu unfiltered.
  useEffect(() => {
    if (!open || !meta) return undefined;
    vehicleJobsRequestRef.current += 1;
    const id = vehicleJobsRequestRef.current;
    const identified = vehicleMode === 'model' ? !!(vehiclePayload.manufacturer && vehiclePayload.model) : hasPlate;
    if (!identified) {
      setVehicleJobs(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const resp = await axios.post('/api/quote-machine/vehicle-jobs/', { vehicle: vehiclePayload });
        if (id !== vehicleJobsRequestRef.current) return;
        setVehicleJobs(resp.data?.vehicle?.known ? resp.data : null);
      } catch (error) {
        if (id === vehicleJobsRequestRef.current) setVehicleJobs(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, meta, vehicleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const hiddenReason = (slug) => vehicleJobs?.jobs?.[slug]?.hidden_reason || null;

  const candidateKey = (row) => `${row.source}:${row.part_id || ''}:${row.code || ''}:${row.article_code || ''}`;
  const offerOf = (pick) => fitting?.jobs?.[keyOf(pick)] || null;
  const mainOf = (pick) => (pick.parts_chosen || []).find((row) => row.kind !== 'extra') || null;
  const extrasOf = (pick) => (pick.parts_chosen || []).filter((row) => row.kind === 'extra');
  const withFit = (row, fit, kind) => ({ ...row, kind, quantity: row.quantity || 1, precision: fit?.precision || null });
  // A typed estimate price does not carry over to real parts.
  const setChosen = (pick, parts_chosen) => updatePick(keyOf(pick), { parts_chosen, parts: null });

  // The main part: one of the offer's main rows. Switching the real part on for the first time
  // also pre-checks the extras the pool usually added, so the sum is what the job usually costs.
  const chooseMain = (pick, row, offer, withSuggested = false) => {
    const kept = extrasOf(pick);
    const extras = withSuggested && kept.length === 0
      ? (offer?.extras || []).filter((r) => r.suggested).map((r) => withFit(r, offer.fit, 'extra'))
      : kept;
    setChosen(pick, row ? [withFit(row, offer?.fit, 'main'), ...extras] : extras);
  };
  const toggleExtra = (pick, row, offer) => {
    const key = candidateKey(row);
    const rest = (pick.parts_chosen || []).filter((r) => candidateKey(r) !== key);
    const had = rest.length !== (pick.parts_chosen || []).length;
    setChosen(pick, had ? rest : [...rest, withFit(row, offer?.fit, 'extra')]);
  };
  const setChosenQuantity = (pick, row, value) => {
    const key = candidateKey(row);
    updatePick(keyOf(pick), {
      parts_chosen: (pick.parts_chosen || []).map((r) => (candidateKey(r) === key ? { ...r, quantity: value } : r)),
    });
  };
  const clearParts = (pick) => setChosen(pick, []);

  // The trust gate decides the default: a trusted exact-vehicle match pre-fills the main part
  // (and its usual extras) unless its price is far off the estimate; anything coarser is offered
  // switched off. The user's own switch always wins.
  useEffect(() => {
    if (!fitting?.jobs) return;
    picks.forEach((pick) => {
      if ((pick.parts_chosen || []).length || partTouchedRef.current.has(keyOf(pick))) return;
      const offer = fitting.jobs[keyOf(pick)];
      const first = offer?.main?.[0];
      if (offer?.fit?.preselect_ok && first && !first.price_flag) chooseMain(pick, first, offer, true);
    });
  }, [fitting]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- free text and dictation
  const interpret = async (payload, source, config) => {
    setInterpreting(true);
    setInterpretError(null);
    setUnmatched([]);
    setAdded([]);
    try {
      const resp = await axios.post('/api/quote-machine/interpret/', payload, config);
      sessionRef.current = resp.data.session_id;
      if (source === 'audio' && resp.data.transcript) setFreeText(resp.data.transcript);
      mergeInterpreted(resp.data, source);
    } catch (error) {
      setInterpretError(error.response?.data?.error || t('quoteMachine.errors.interpret'));
    } finally {
      setInterpreting(false);
    }
  };

  const interpretText = () => {
    const text = freeText.trim();
    if (!text || interpreting) return;
    interpret({ text, vehicle: vehiclePayload, session_id: sessionRef.current, language: i18n.language }, 'text');
  };

  const recorder = useAudioRecorder({
    onRecorded: (blob) => {
      const type = (blob.type || 'audio/webm').split(';')[0];
      const form = new FormData();
      form.append('audio', blob, `sanelu.${AUDIO_EXTENSION[type] || 'webm'}`);
      form.append('vehicle', JSON.stringify(vehiclePayload));
      form.append('language', i18n.language);
      if (sessionRef.current) form.append('session_id', sessionRef.current);
      interpret(form, 'audio');
    },
  });

  // ---------------------------------------------------------------- create
  // `creating` holds which button is working ('planned', 'quote' or 'send') so only that one
  // shows the spinner. With `send`, the quote opens with its send dialog already up
  // (MaintenanceProcessView reads ?sendQuote=1 once the work order has loaded).
  const create = async (state, { send = false } = {}) => {
    if (creating || pricing || !quote) return;
    setCreating(send ? 'send' : state);
    setCreateError(null);
    try {
      const resp = await axios.post('/api/quote-machine/create/', {
        session_id: sessionRef.current,
        vehicle,
        customer_id: customer?.id || null,
        state,
      });
      addCreatedMaintenance?.(resp.data);
      handleClose();
      navigate(`/maintenances/${resp.data.id}${send ? '?sendQuote=1' : ''}`);
    } catch (error) {
      setCreateError(error.response?.data?.error || t('quoteMachine.errors.create'));
    } finally {
      setCreating(false);
    }
  };

  const close = () => {
    recorder.cancel();
    handleClose();
  };

  // ---------------------------------------------------------------- render helpers
  // Search finds everything. Otherwise jobs the pool has never seen on this model (or the half
  // of an exclusive pair the model does not have) are dropped unless picked or revealed; the
  // quick menu is the top of what remains.
  const hiddenCount = useMemo(() => {
    if (!vehicleJobs?.jobs) return 0;
    return (meta?.templates || []).filter((template) => hiddenReason(template.slug)).length;
  }, [meta, vehicleJobs]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleTemplates = useMemo(() => {
    const all = meta?.templates || [];
    const needle = search.trim().toLowerCase();
    if (needle) return all.filter((template) => template.name.toLowerCase().includes(needle));
    const picked = new Set(picks.map((pick) => pick.template));
    const offered = showUnknown
      ? all
      : all.filter((template) => !hiddenReason(template.slug) || picked.has(template.slug));
    if (showAll) return offered;
    return [...offered].sort((a, b) => (b.used - a.used) || (a.position - b.position)).slice(0, QUICK_COUNT);
  }, [meta, search, showAll, showUnknown, vehicleJobs, picksKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = showAll && !search.trim();
  const withVat = quote ? quote.entered_with_vat : meta?.entered_with_vat;
  const side = (amounts) => (withVat ? amounts.gross : amounts.net);
  const sideLabel = withVat ? t('incl. VAT') : t('excl. VAT');
  const canCreate = !!quote && !pricing && !creating && picks.length > 0 && hasPlate && vehicleKnown;
  // Sending needs someone to send to: the send dialog has no customer picker of its own.
  const canSend = canCreate && !!customer;
  const totals = quote?.totals;
  const totalHours = (quote?.jobs || []).reduce(
    (sum, job) => sum + (job.priced && job.labour?.form === 'hours' ? parseFloat(job.labour.hours) || 0 : 0), 0
  );
  const vehicleDone = vehicleKnown;

  const overline = (text, sx) => (
    <Typography
      variant="overline"
      sx={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1.4, color: 'text.secondary', ...sx }}
    >
      {text}
    </Typography>
  );

  const caption = (text, { icon: Icon = InfoOutlinedIcon, tone = 'text.secondary', testId, sx } = {}) => (
    <Typography
      variant="caption"
      component="div"
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, lineHeight: 1.45, color: tone, ...sx }}
    >
      {Icon && <Icon sx={{ fontSize: 15, mt: '1px', flexShrink: 0, color: tone === 'text.secondary' ? 'text.disabled' : 'inherit' }} />}
      <span>{text}</span>
    </Typography>
  );

  const stepTitle = (number, title, done, aside) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 12, fontWeight: 700,
          bgcolor: done ? 'status.completedBg' : 'primary.main', color: done ? 'success.dark' : 'primary.contrastText',
        }}
      >
        {done ? <CheckIcon sx={{ fontSize: 15 }} /> : number}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ flex: 1 }} />
      {aside}
    </Box>
  );

  const templateChip = (template, small = false) => {
    const selected = picks.some((pick) => pick.template === template.slug);
    const reason = hiddenReason(template.slug);
    const chip = (
      <Chip
        key={template.slug}
        label={template.name}
        clickable
        color={selected ? 'primary' : 'default'}
        variant={selected ? 'filled' : 'outlined'}
        icon={selected ? <CheckIcon sx={{ fontSize: 15 }} /> : undefined}
        onClick={() => togglePick(template.slug)}
        data-testid={`quote-machine-template-${template.slug}`}
        data-hidden-reason={reason || undefined}
        sx={{
          height: small ? 30 : 34,
          fontSize: small ? 13 : 13.5,
          fontWeight: selected ? 600 : 500,
          bgcolor: selected ? 'primary.main' : 'background.paper',
          borderColor: selected ? 'primary.main' : '#cbd5e1',
          '& .MuiChip-icon': { color: 'inherit', ml: '8px', mr: '-4px' },
          ...(selected ? {} : { '&:hover': { borderColor: 'secondary.main', bgcolor: 'background.default' } }),
          ...(reason && !selected ? { color: 'text.disabled', borderStyle: 'dashed' } : {}),
        }}
      />
    );
    if (!reason || selected) return chip;
    return (
      <Tooltip
        key={template.slug}
        title={t(reason === 'exclusive' ? 'quoteMachine.evidence.notTypicalForModel' : 'quoteMachine.evidence.notForModel')}
        arrow
        enterTouchDelay={0}
      >
        {chip}
      </Tooltip>
    );
  };

  const evidenceText = (evidence) => {
    if (!evidence) return null;
    if (evidence.source === 'nearest') return null; // the caption below says where the number is from
    // Where the number rests, in words: the counts behind it stay inside the engine.
    if (evidence.source === 'pool') return t('quoteMachine.evidence.pool');
    if (evidence.pool_jobs > 0) return t('quoteMachine.evidence.mixed');
    return t('quoteMachine.evidence.own');
  };

  const priceInput = (pick, field, shown, testId) => (
    <DecimalField
      value={pick[field] !== null ? pick[field] : shown}
      onChange={(value) => updatePick(keyOf(pick), { [field]: value })}
      onBlur={() => { if (pick[field] === '') updatePick(keyOf(pick), { [field]: null }); }}
      size="small"
      variant="standard"
      inputProps={{ style: { textAlign: 'right', fontWeight: 600 }, 'data-testid': testId, 'aria-label': sideLabel }}
      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment>, sx: { ...numFont } }}
      sx={{ width: 108, flexShrink: 0 }}
    />
  );

  const priceCell = (pick, field, amounts, testId, muted = false) => (
    meta?.can_edit_prices
      ? priceInput(pick, field, side(amounts), testId)
      : (
        <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: muted ? 'text.secondary' : 'text.primary', ...numFont }}>
          {formatPriceWithCurrency(side(amounts))}
        </Typography>
      )
  );

  const priceRow = (label, cell, { testId, first = false } = {}) => (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minHeight: 44, py: 0.5,
        borderTop: first ? 0 : '1px solid', borderColor: 'divider',
      }}
    >
      <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 500, minWidth: 0 }} data-testid={testId}>
        {label}
      </Typography>
      {cell}
    </Box>
  );

  const vatRate = parseFloat(quote?.vat_rate) || 25.5;
  // With a real part on the line the editable number is its UNIT price (the line is qty x unit).
  const unitOf = (parts) => ({
    net: parts.spec.unit_price,
    gross: parts.spec.unit_price_incl_vat ?? calcPriceInclVat(parts.spec.unit_price, vatRate),
  });

  const candidateRow = ({ pick, row, offer, kind, checked, onPick, testId }) => {
    const meta_ = [
      row.brand,
      row.code ? <Box component="span" key="code" sx={{ ...codeFont }}>{row.code}</Box> : null,
      row.vendor,
      kind === 'extra' ? t(row.suggested ? 'quoteMachine.fitting.seenOften' : 'quoteMachine.fitting.seenSometimes') : null,
    ].filter(Boolean);
    return (
      <Box
        key={candidateKey(row)}
        data-testid={testId}
        sx={{ '& + &': { borderTop: '1px solid', borderColor: 'divider' }, bgcolor: checked ? 'rgba(49, 130, 206, 0.06)' : 'background.paper' }}
      >
        <Box
          onClick={onPick}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25, cursor: 'pointer', px: 1.5, py: 1.25,
            '&:hover': { bgcolor: 'background.default' },
          }}
        >
          {kind === 'main'
            ? <Radio size="small" checked={checked} sx={{ p: 0, mt: '1px' }} />
            : <Checkbox size="small" checked={checked} sx={{ p: 0, mt: '1px' }} inputProps={{ 'data-testid': `quote-machine-extra-${tidOf(pick)}` }} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: checked || kind === 'main' ? 'text.primary' : 'text.secondary' }}>
                {row.name || row.code}
              </Typography>
              {row.price_flag && (
                <Tooltip title={t('quoteMachine.fitting.priceFlagHelp')} arrow enterTouchDelay={0}>
                  <Chip
                    size="small"
                    icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={t(`quoteMachine.fitting.priceFlag.${row.price_flag}`)}
                    data-testid={`quote-machine-price-flag-${tidOf(pick)}`}
                    sx={{
                      height: 22, fontSize: 12, fontWeight: 600, color: '#b45309', bgcolor: '#fef3e2',
                      border: '1px solid #fbd38d', '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            {meta_.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {meta_.map((piece, index) => (
                  <React.Fragment key={index}>{index > 0 && ' · '}{piece}</React.Fragment>
                ))}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: checked ? 'text.primary' : 'text.secondary', ...numFont }}>
            {formatPriceWithCurrency(withVat ? calcPriceInclVat(row.price, vatRate) : row.price)}
          </Typography>
        </Box>
        {checked && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5.25, pr: 1.5, pb: 1.25, mt: -0.5 }}>
            <Typography variant="caption" color="text.secondary">{t('quoteMachine.swap.quantity')}</Typography>
            <DecimalField
              value={(pick.parts_chosen || []).find((r) => candidateKey(r) === candidateKey(row))?.quantity ?? 1}
              onChange={(value) => setChosenQuantity(pick, row, value)}
              size="small"
              sx={{ width: 72, '& .MuiInputBase-input': { py: 0.5, textAlign: 'center', ...numFont } }}
              inputProps={{ 'data-testid': kind === 'main' ? `quote-machine-part-quantity-${tidOf(pick)}` : `quote-machine-extra-quantity-${tidOf(pick)}` }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const listBox = (children) => (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden', bgcolor: 'background.paper' }}>
      {children}
    </Box>
  );

  // The deliberate step at the foot of a job card: a quiet switch row that opens into an inset
  // panel with the main part and its usual companions.
  const fittingBlock = (pick, job) => {
    const offer = offerOf(pick);
    if (!offer || !job?.priced) return null;
    const main = offer.main || [];
    const extras = offer.extras || [];
    const rows = offer.parts || [];
    const unpriced = rows.some((row) => !row.purchasable && row.source !== 'advisory');
    const unpricedMain = rows.some((row) => row.kind === 'main' && !row.purchasable && row.source !== 'advisory');
    const canProbe = !!fitting?.can_probe && !fitting?.probed;
    // No priced main part: nothing to switch to. The block stays only when a supplier probe could
    // still price one of the job's own parts.
    if (main.length === 0 && !(unpricedMain && canProbe)) return null;
    const fit = offer.fit;
    const chosenMain = mainOf(pick);
    const on = !!chosenMain;
    const mainKey = chosenMain ? candidateKey(chosenMain) : null;
    const extraKeys = new Set(extrasOf(pick).map(candidateKey));
    const toggle = (checked) => {
      partTouchedRef.current.add(keyOf(pick));
      if (checked) chooseMain(pick, main[0], offer, true);
      else clearParts(pick);
    };
    const probeButton = canProbe && unpriced && (
      <Button
        size="small"
        variant="outlined"
        startIcon={probing ? <CircularProgress size={12} /> : <LocalShippingOutlinedIcon />}
        disabled={probing}
        onClick={() => fetchFitting(true)}
        data-testid={`quote-machine-probe-${tidOf(pick)}`}
        sx={{ textTransform: 'none', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        {probing ? t('quoteMachine.swap.probing') : t('quoteMachine.swap.probe')}
      </Button>
    );
    const FitIcon = fit.trusted ? VerifiedOutlinedIcon : DirectionsCarOutlinedIcon;
    return (
      <Box
        sx={{ mt: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: on ? 'background.default' : 'transparent', transition: 'background-color 200ms' }}
        data-testid={`quote-machine-fitting-${tidOf(pick)}`}
      >
        {/* The fit chip drops under the text on a narrow card rather than squeezing the title. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.25, flexWrap: 'wrap' }}>
          <Switch
            size="small"
            checked={on}
            disabled={main.length === 0}
            onChange={(event) => toggle(event.target.checked)}
            inputProps={{ 'data-testid': `quote-machine-use-part-${tidOf(pick)}` }}
          />
          <Box
            sx={{ flex: 1, minWidth: 180, cursor: main.length ? 'pointer' : 'default' }}
            onClick={() => { if (main.length) toggle(!on); }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: main.length ? 'text.primary' : 'text.secondary' }}>
              {t('quoteMachine.fitting.useReal')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
              data-testid={main.length === 0 ? `quote-machine-no-priced-${tidOf(pick)}` : undefined}
            >
              {main.length === 0 ? t('quoteMachine.fitting.noPriced') : t(on ? 'quoteMachine.fitting.onHint' : 'quoteMachine.fitting.offHint')}
            </Typography>
          </Box>
          <Tooltip title={t(`aiWorkOrder.precisionHelp.${fit.precision}`)} arrow enterTouchDelay={0}>
            <Chip
              size="small"
              icon={<FitIcon sx={{ fontSize: 14 }} />}
              label={t(`quoteMachine.fitting.precision.${fit.precision}`)}
              data-testid={`quote-machine-fit-${tidOf(pick)}`}
              sx={{
                height: 22, fontSize: 12, fontWeight: 600, flexShrink: 0, maxWidth: '100%', ml: 'auto',
                color: fit.trusted ? 'success.dark' : 'primary.main',
                bgcolor: fit.trusted ? 'status.completedBg' : 'background.paper',
                border: '1px solid', borderColor: fit.trusted ? '#9ae6b4' : 'divider',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Tooltip>
        </Box>
        {on && (
          <Box sx={{ px: 2.25, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Box>
              {overline(t('quoteMachine.fitting.mainTitle'), { mb: 0.75 })}
              {listBox(main.map((row) => candidateRow({
                pick, row, offer, kind: 'main', checked: candidateKey(row) === mainKey,
                onPick: () => { partTouchedRef.current.add(keyOf(pick)); chooseMain(pick, row, offer); },
                testId: `quote-machine-part-option-${tidOf(pick)}`,
              })))}
            </Box>
            {extras.length > 0 && (
              <Box>
                {overline(t('quoteMachine.fitting.extrasTitle'), { mb: 0.75 })}
                {listBox(extras.map((row) => candidateRow({
                  pick, row, offer, kind: 'extra', checked: extraKeys.has(candidateKey(row)),
                  onPick: () => { partTouchedRef.current.add(keyOf(pick)); toggleExtra(pick, row, offer); },
                  testId: `quote-machine-extra-option-${tidOf(pick)}`,
                })))}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              {caption(t('quoteMachine.fitting.hint'), { sx: { flex: 1, minWidth: 200 } })}
              {probeButton}
            </Box>
          </Box>
        )}
        {!on && probeButton && <Box sx={{ px: 2.25, pb: 1.5 }}>{probeButton}</Box>}
      </Box>
    );
  };

  const tidOf = (pick) => pickTestId(templatesBySlug[pick.template], pick.answers);
  // The priced entry of a pick: the server names it by the same pick identity.
  const jobOf = (pick) => (quote?.jobs || []).find((entry) => entry.pick === keyOf(pick)) || null;
  // Cards: one per template, holding its picks (one per axle for a job done per axle).
  const groups = [];
  picks.forEach((pick) => {
    let group = groups.find((item) => item.slug === pick.template);
    if (!group) {
      group = { slug: pick.template, picks: [] };
      groups.push(group);
    }
    group.picks.push(pick);
  });

  const tierChip = (job) => {
    const tier = TIER_STYLE[job.tier] || TIER_STYLE.none;
    return (
      <Chip
        size="small"
        icon={<tier.Icon sx={{ fontSize: 14 }} />}
        label={t(`quoteMachine.tier.${job.tier}`)}
        sx={{
          height: 22, fontSize: 12, fontWeight: 600, color: tier.color, bgcolor: tier.bgcolor,
          border: '1px solid', borderColor: tier.borderColor, '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
    );
  };

  const evidenceLine = (job) => {
    const evidence = evidenceText(job.evidence);
    if (!evidence) return null;
    const EvidenceIcon = job.evidence?.source === 'pool' ? GroupsOutlinedIcon : HistoryOutlinedIcon;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <EvidenceIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        {evidence}
      </Typography>
    );
  };

  // The axle control of a card: front, rear or both axles (both = the job twice on the quote).
  const axleControl = (template, group) => {
    const question = template.questions.find((item) => item.id === AXLE);
    const axles = group.picks.map((pick) => pick.answers[AXLE]);
    const value = axles.length > 1 ? AXLE_BOTH : axles[0];
    return (
      <Box key={AXLE} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.primary', flex: 1, minWidth: 140 }}>{question.text}</Typography>
        <ToggleButtonGroup exclusive size="small" value={value} onChange={(event, next) => setAxle(template.slug, next)} sx={SEGMENT_SX}>
          {question.answers.map((option) => (
            <ToggleButton key={option.id} value={option.id} data-testid={`quote-machine-axle-${template.slug}-${option.id}`}>
              {option.text}
            </ToggleButton>
          ))}
          <ToggleButton value={AXLE_BOTH} data-testid={`quote-machine-axle-${template.slug}-both`}>
            {t('quoteMachine.axleBoth')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };

  // The priced part of one pick: its labour and parts rows, then the real-parts step.
  const pickBlock = (pick, job, template, labelled) => {
    const pid = tidOf(pick);
    const fittingNode = job && job.priced && job.parts ? fittingBlock(pick, job) : null;
    const axleText = labelled
      ? template.questions.find((item) => item.id === AXLE)?.answers.find((option) => option.id === pick.answers[AXLE])?.text
      : null;
    return (
      <Box key={keyOf(pick)} data-testid={`quote-machine-pick-${pid}`}>
        {labelled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 2.25, mt: 1.5, flexWrap: 'wrap' }}>
            {overline(axleText || pick.answers[AXLE], { color: 'text.primary' })}
            {job && tierChip(job)}
            {job && evidenceLine(job)}
          </Box>
        )}
        {job && job.priced && (
          <Box
            sx={{ mx: 2.25, mt: labelled ? 0.5 : 1.5, borderTop: '1px solid', borderColor: 'divider', color: job.to_be_confirmed ? 'text.secondary' : 'text.primary' }}
            data-testid={job.to_be_confirmed ? `quote-machine-to-be-confirmed-${pid}` : undefined}
          >
            {priceRow(
              job.labour.form === 'hours'
                ? t('quoteMachine.labourHours', { hours: formatPrice(job.labour.hours) })
                : t('quoteMachine.labourFixed'),
              priceCell(pick, 'labour', job.labour, `quote-machine-labour-${pid}`, job.to_be_confirmed),
              { first: true }
            )}
            {job.parts && (
              <>
                {priceRow(
                  job.parts.part && parseFloat(job.parts.spec.quantity) !== 1
                    ? `${formatQuantity(job.parts.spec.quantity)} × ${job.parts.label}`
                    : job.parts.label,
                  priceCell(pick, 'parts', job.parts.part ? unitOf(job.parts) : job.parts, `quote-machine-parts-${pid}`, job.to_be_confirmed),
                  { testId: `quote-machine-parts-label-${pid}` }
                )}
                {/* Real parts: one caption per line (the main line's price is the unit price
                    edited above), and the sum when there is more than one number to add up. */}
                {((job.parts.lines || []).length > 1 || (job.parts.part && parseFloat(job.parts.spec.quantity) !== 1)) && (
                  <Box sx={{ pl: 1.5, pb: 1, mt: -0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {(job.parts.lines || []).slice(1).map((line) => (
                      <Box
                        key={candidateKey(line.part)}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                        data-testid={`quote-machine-parts-line-${pid}`}
                      >
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                          {parseFloat(line.spec.quantity) !== 1 ? `${formatQuantity(line.spec.quantity)} × ${line.label}` : line.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ...numFont }}>
                          {formatPriceWithCurrency(side(line))}
                        </Typography>
                      </Box>
                    ))}
                    {job.parts.part && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', textAlign: 'right', fontWeight: 600, color: 'text.primary', ...numFont }}
                        data-testid={`quote-machine-parts-total-${pid}`}
                      >
                        {t('quoteMachine.fitting.total')} {formatPriceWithCurrency(side(job.parts))}
                      </Typography>
                    )}
                  </Box>
                )}
                {job.parts.part && meta?.can_edit_prices && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', pb: 1 }}>
                    {t('quoteMachine.fitting.typedUnitHint')}
                  </Typography>
                )}
              </>
            )}
            {job.evidence && job.evidence.model_specific === false && caption(
              t(job.evidence.source === 'nearest' ? 'quoteMachine.evidence.nearest' : 'quoteMachine.evidence.notModelSpecific'),
              { testId: `quote-machine-not-model-specific-${pid}`, sx: { pb: 1.25, pt: 0.25 } }
            )}
          </Box>
        )}

        {fittingNode || <Box sx={{ height: 8 }} />}

        {job && !job.priced && (
          <Typography variant="body2" color="text.secondary" sx={{ mx: 2.25, mb: 1 }} data-testid={`quote-machine-no-price-${pid}`}>
            {t('quoteMachine.tier.none')}
          </Typography>
        )}
      </Box>
    );
  };

  // Jobs usually on the same visit as this one that are not on the quote: one click adds one.
  const usuallyChips = (template) => {
    const offers = (template.usually_with || []).filter(
      (other) => templatesBySlug[other.template] && !picksOf(other.template).length
    );
    if (!offers.length) return null;
    return (
      <Box sx={{ mx: 2.25, mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('quoteMachine.usuallyWith')}</Typography>
        {offers.map((other) => (
          <Chip
            key={other.template}
            size="small"
            variant="outlined"
            icon={<AddIcon />}
            label={`${templatesBySlug[other.template].name} · ${Math.round(other.share * 100)} %`}
            onClick={() => addPick(other.template, {}, 'usually', false, other.share)}
            data-testid={`quote-machine-usually-${template.slug}-${other.template}`}
          />
        ))}
      </Box>
    );
  };

  const jobCard = (group) => {
    const template = templatesBySlug[group.slug];
    if (!template) return null;
    const single = group.picks.length === 1 ? group.picks[0] : null;
    const singleJob = single ? jobOf(single) : null;
    const questions = template.questions.filter((question) => question.id !== AXLE);
    const usuallyShare = group.picks[0]?.source === 'usually' ? group.picks[0].usually_share : null;
    return (
      <Box
        key={group.slug}
        data-testid={`quote-machine-selected-${group.slug}`}
        sx={{
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,.05)', overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pt: 1.75, pr: 1.25, pl: 2.25 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.3 }}>{template.name}</Typography>
            {singleJob && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                {tierChip(singleJob)}
                {evidenceLine(singleJob)}
              </Box>
            )}
            {usuallyShare !== null && usuallyShare !== undefined && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
                data-testid={`quote-machine-usually-added-${group.slug}`}
              >
                {t('quoteMachine.usuallyAdded', { share: Math.round(usuallyShare * 100) })}
              </Typography>
            )}
          </Box>
          <Tooltip title={t('quoteMachine.remove')}>
            <IconButton
              size="small"
              onClick={() => removeTemplate(group.slug)}
              aria-label={t('quoteMachine.remove')}
              data-testid={`quote-machine-remove-${group.slug}`}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {template.questions.length > 0 && (
          <Box sx={{ mx: 2.25, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {hasAxle(template) && axleControl(template, group)}
            {questions.map((question) => (
              <Box key={question.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.primary', flex: 1, minWidth: 140 }}>{question.text}</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={group.picks[0].answers[question.id]}
                  onChange={(event, value) => answer(group.slug, question.id, value)}
                  sx={SEGMENT_SX}
                >
                  {question.answers.map((option) => (
                    <ToggleButton key={option.id} value={option.id} data-testid={`quote-machine-answer-${question.id}-${option.id}`}>
                      {option.text}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            ))}
          </Box>
        )}

        {usuallyChips(template)}

        {group.picks.map((pick) => pickBlock(pick, jobOf(pick), template, !single))}
      </Box>
    );
  };


  // ---------------------------------------------------------------- the three steps
  const vehicleStep = (
    <Box component="section">
      {stepTitle(1, t('quoteMachine.vehicle'), vehicleDone, (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={vehicleMode}
          onChange={(event, value) => value && setVehicleMode(value)}
          sx={SEGMENT_SX}
        >
          <ToggleButton value="plate">{t('quoteMachine.plateTab')}</ToggleButton>
          <ToggleButton value="model" data-testid="quote-machine-vehicle-model-tab">{t('quoteMachine.modelTab')}</ToggleButton>
        </ToggleButtonGroup>
      ))}
      {vehicleMode === 'plate' && hasPlate && !vehicleKnown && (
        <Alert severity="info" sx={{ mb: 1.25 }} data-testid="quote-machine-vehicle-needed">
          {t('quoteMachine.lookupNeeded')}
        </Alert>
      )}
      {vehicleMode === 'plate' ? (
        <VehicleRegistrationField
          vehicle={vehicle}
          onChange={setVehicle}
          onLastCustomer={(last) => {
            if (!last) return;
            setCustomer((current) => {
              if (current && !autoCustomerRef.current) return current;
              autoCustomerRef.current = true;
              return last;
            });
          }}
          onExistingVehicle={(existing) => {
            if (!existing && autoCustomerRef.current) {
              autoCustomerRef.current = false;
              setCustomer(null);
            }
          }}
          autoFocus
          dataTestId="quote-machine-registration"
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              options={Object.keys(meta?.vehicle_groups || {})}
              value={typed.make}
              onChange={(event, value) => setTyped({ ...typed, make: value, model: null })}
              renderInput={(params) => <TextField {...params} label={t('quoteMachine.make')} />}
              sx={{ flex: 1, minWidth: 160 }}
            />
            <Autocomplete
              size="small"
              options={(meta?.vehicle_groups || {})[typed.make] || []}
              value={typed.model}
              disabled={!typed.make}
              onChange={(event, value) => setTyped({ ...typed, model: value })}
              renderInput={(params) => <TextField {...params} label={t('quoteMachine.model')} />}
              sx={{ flex: 1.4, minWidth: 160 }}
            />
            <DecimalField
              size="small"
              label={t('quoteMachine.engineSize')}
              value={typed.litres}
              onChange={(value) => setTyped({ ...typed, litres: value })}
              maxDecimals={1}
              max={9}
              sx={{ width: 150 }}
            />
          </Box>
          {caption(t('quoteMachine.plateNeeded'))}
        </Box>
      )}
    </Box>
  );

  const jobsStep = (
    <Box component="section">
      {stepTitle(2, t('quoteMachine.jobs'), picks.length > 0, picks.length > 0 && (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('quoteMachine.jobsPicked', { count: picks.length })}</Typography>
      ))}
      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('quoteMachine.searchJobs')}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')} aria-label={t('Clear')}><CloseIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{ 'data-testid': 'quote-machine-job-search' }}
        sx={{ mb: 1.5 }}
      />
      {grouped ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {meta?.groups.map((group) => {
            const inGroup = visibleTemplates.filter((template) => template.group === group.id);
            if (!inGroup.length) return null;
            return (
              <Box key={group.id}>
                {overline(group.name, { mb: 1 })}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{inGroup.map((template) => templateChip(template, true))}</Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {visibleTemplates.map((template) => templateChip(template))}
          {search.trim() && visibleTemplates.length === 0 && (
            <Typography variant="body2" sx={{ py: 0.75 }}>{t('quoteMachine.noSearchHits')}</Typography>
          )}
        </Box>
      )}
      {!search.trim() && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            size="small"
            color="secondary"
            startIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAll(!showAll)}
            data-testid="quote-machine-show-all"
            sx={{ textTransform: 'none', fontWeight: 600, px: 0.75 }}
          >
            {t(showAll ? 'quoteMachine.showLess' : 'quoteMachine.showAll')}
          </Button>
          {hiddenCount > 0 && (
            <Button
              size="small"
              startIcon={showUnknown ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              onClick={() => setShowUnknown(!showUnknown)}
              data-testid="quote-machine-show-unknown"
              sx={{ textTransform: 'none', fontWeight: 600, px: 0.75, color: 'text.secondary', textAlign: 'left' }}
            >
              {t(showUnknown ? 'quoteMachine.hideUnknownForModel' : 'quoteMachine.showUnknownForModel')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  const freeTextStep = (
    <Box component="section">
      {stepTitle(3, t('quoteMachine.freeTextLabel'), false, null)}
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          value={freeText}
          onChange={(event) => setFreeText(event.target.value.slice(0, 1000))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              interpretText();
            }
          }}
          placeholder={t('quoteMachine.freeTextPlaceholder')}
          disabled={recorder.recording || interpreting}
          inputProps={{ 'data-testid': 'quote-machine-free-text' }}
          InputProps={recorder.supported ? {
            sx: { alignItems: 'flex-start' },
            endAdornment: (
              <InputAdornment position="end" sx={{ mt: 0.5 }}>
                <Tooltip title={t(recorder.recording ? 'quoteMachine.stopRecording' : 'quoteMachine.record')}>
                  <span>
                    <IconButton
                      size="small"
                      color={recorder.recording ? 'error' : 'default'}
                      onClick={recorder.recording ? recorder.stop : recorder.start}
                      disabled={interpreting}
                      aria-label={t(recorder.recording ? 'quoteMachine.stopRecording' : 'quoteMachine.record')}
                      data-testid="quote-machine-mic"
                      sx={recorder.recording ? { animation: 'quoteMachinePulse 1s infinite', '@keyframes quoteMachinePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.45 } } } : undefined}
                    >
                      {recorder.recording ? <StopIcon /> : <MicIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          } : undefined}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Button
          variant="outlined"
          startIcon={interpreting ? <CircularProgress size={16} /> : <AutoAwesomeOutlinedIcon />}
          onClick={interpretText}
          disabled={!freeText.trim() || interpreting || recorder.recording}
          data-testid="quote-machine-interpret"
          sx={{ textTransform: 'none', height: 40, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          {t('quoteMachine.interpret')}
        </Button>
      </Box>
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {recorder.recording && (
          <Typography variant="caption" color="error" data-testid="quote-machine-recording">
            {t('quoteMachine.recording', { seconds: recorder.seconds, max: MAX_RECORDING_SECONDS })}
          </Typography>
        )}
        {interpreting && !recorder.recording && (
          <Typography variant="caption" color="text.secondary">{t('quoteMachine.interpreting')}</Typography>
        )}
        {recorder.error && (
          <Alert severity="warning" onClose={recorder.clearError}>
            {t(recorder.error === 'denied' ? 'quoteMachine.micDenied' : 'quoteMachine.micFailed')}
          </Alert>
        )}
        {interpretError && <Alert severity="warning" onClose={() => setInterpretError(null)}>{interpretError}</Alert>}
        {added.length > 0 && caption(t('quoteMachine.added', { items: added.join(', ') }), { icon: CheckCircleOutlineIcon, tone: 'success.dark' })}
        {unmatched.length > 0 && caption(
          t('quoteMachine.unmatched', { items: unmatched.join(', ') }),
          { icon: HelpOutlineIcon, tone: '#b45309', testId: 'quote-machine-unmatched' }
        )}
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------- the price panel
  const summary = picks.length > 0 && !vehicleKnown ? (
    <Box sx={{ px: 3, py: { xs: 3, md: 3.5 }, textAlign: 'center' }} data-testid="quote-machine-vehicle-needed-panel">
      {overline(`${t('quoteMachine.priceTitle')}, ${t('incl. VAT')}`, { mb: 1.75 })}
      <Box
        sx={{
          width: 52, height: 52, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', mb: 1.25,
        }}
      >
        <DirectionsCarOutlinedIcon sx={{ fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{t('quoteMachine.lookupNeededTitle')}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.45 }}>{t('quoteMachine.lookupNeeded')}</Typography>
    </Box>
  ) : picks.length === 0 ? (
    <Box sx={{ px: 3, py: { xs: 3, md: 3.5 }, textAlign: 'center' }}>
      {overline(`${t('quoteMachine.priceTitle')}, ${t('incl. VAT')}`, { mb: 1.75 })}
      <Box
        sx={{
          width: 52, height: 52, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', mb: 1.25,
        }}
      >
        <CalculateOutlinedIcon sx={{ fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{t('quoteMachine.pickJobs')}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.45 }}>{t('quoteMachine.pickJobsHelp')}</Typography>
    </Box>
  ) : (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2.25, md: 2.75 }, pb: 2 }}>
      {overline(`${t('quoteMachine.priceTitle')}, ${t('incl. VAT')}`, { mb: 1 })}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography
          data-testid="quote-machine-total"
          sx={{
            fontSize: { xs: 34, md: 40 }, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1, whiteSpace: 'nowrap',
            opacity: pricing ? 0.45 : 1, transition: 'opacity 150ms', ...numFont,
          }}
        >
          {totals ? formatPriceWithCurrency(totals.gross) : '…'}
        </Typography>
        {pricing && <CircularProgress size={16} sx={{ alignSelf: 'center' }} />}
        {totals?.range && (
          <Typography variant="body2" data-testid="quote-machine-range" sx={{ whiteSpace: 'nowrap', ...numFont }}>
            {t('quoteMachine.typically', {
              low: formatPriceWithCurrency(totals.range.low),
              high: formatPriceWithCurrency(totals.range.high),
            })}
          </Typography>
        )}
      </Box>
      {totals && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25, flexWrap: 'wrap', ...numFont }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('quoteMachine.netTotal', { amount: formatPriceWithCurrency(totals.net) })}</Typography>
          {totalHours > 0 && (
            <>
              <Box component="span" sx={{ color: 'divider' }}>·</Box>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('quoteMachine.labourHours', { hours: formatPrice(totalHours) })}</Typography>
            </>
          )}
          <Box component="span" sx={{ color: 'divider' }}>·</Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('quoteMachine.jobsCount', { count: picks.length })}</Typography>
        </Box>
      )}
      {priceError && <Alert severity="error" sx={{ mt: 1.5 }}>{priceError}</Alert>}
    </Box>
  );

  const panelFooter = picks.length > 0 && (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: 0.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {totals?.pientarvikelisa && (
        <Box
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25,
            borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{t('quoteMachine.pientarvikelisa')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, ...numFont }}>
            {formatPriceWithCurrency(side(totals.pientarvikelisa))}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* The headline is gross; the rows follow the workshop's entry side of VAT. Said once,
            for everyone, so a net-entry workshop's rows are not read as purchase prices under
            a gross total. */}
        {caption(t('quoteMachine.linesSide', { side: sideLabel }), { icon: null, testId: 'quote-machine-lines-side' })}
        {meta?.can_edit_prices && caption(t('quoteMachine.editHint', { side: sideLabel }), { icon: null })}
        {totals?.unpriced > 0 && caption(t('quoteMachine.unpricedNote', { count: totals.unpriced }), { icon: null })}
        {quote?.notes?.overlap && caption(t('quoteMachine.overlapNote'), { icon: null })}
        {quote?.notes?.second_axle && caption(t('quoteMachine.secondAxleNote'), { icon: null, testId: 'quote-machine-second-axle-note' })}
        {quote?.notes?.no_hourly_rate && caption(t('quoteMachine.noHourlyRate'), { icon: null })}
        {hasDiscount(customer) && caption(t('quoteMachine.discountNote'), { icon: null })}
      </Box>
    </Box>
  );

  const customerPicker = (
    <Box data-testid="quote-machine-customer">
      <CustomerAutocomplete
        value={customer}
        onChange={(value) => { autoCustomerRef.current = false; setCustomer(value); }}
        label={t('quoteMachine.customer')}
      />
    </Box>
  );
  const createHint = createError
    ? <Alert severity="error" sx={{ py: 0 }}>{createError}</Alert>
    : picks.length > 0 && !hasPlate
      ? caption(t('quoteMachine.plateNeeded'))
      : canCreate && !customer
        ? caption(t('quoteMachine.customerNeededToSend'))
        : null;
  const workOrderButton = (
    <Button
      variant="outlined"
      disabled={!canCreate}
      onClick={() => create('planned')}
      data-testid="quote-machine-create-work-order"
      sx={{ textTransform: 'none' }}
    >
      {t('quoteMachine.createWorkOrder')}
    </Button>
  );
  const quoteButton = (
    <Button
      variant="outlined"
      color="secondary"
      disabled={!canCreate}
      onClick={() => create('quote')}
      data-testid="quote-machine-create-quote"
      sx={{ textTransform: 'none', fontWeight: 600 }}
    >
      {creating === 'quote' ? <CircularProgress size={20} color="inherit" /> : t('quoteMachine.createQuote')}
    </Button>
  );
  // The phone-call flow: the quote is created and its send dialog opens on the work order,
  // so the price reaches the customer while the call is still on.
  const sendButton = (
    <Button
      variant="contained"
      color="secondary"
      disabled={!canSend}
      onClick={() => create('quote', { send: true })}
      data-testid="quote-machine-create-and-send"
      sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
    >
      {creating === 'send' ? <CircularProgress size={20} color="inherit" /> : t('quoteMachine.createAndSend')}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      data-testid="quote-machine-dialog"
      sx={{
        // A focused field inside this window: a thin accent border with a soft ring, not the
        // app's 2 px navy outline, which reads as a black frame on the white workspace.
        '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 3px rgba(49, 130, 206, 0.14)' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main', borderWidth: 1 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.75, px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 2.5 },
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 40, height: 40, borderRadius: '11px', bgcolor: 'primary.main', color: 'primary.contrastText',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <BoltIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={{ display: 'block', fontSize: { xs: 17, md: 19 }, fontWeight: 700, letterSpacing: '-.3px', lineHeight: 1.2 }}>
            {t('quoteMachine.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.375, lineHeight: 1.4 }}>{t('quoteMachine.promise')}</Typography>
        </Box>
        {canEditSettings && (
          <Tooltip title={t('quoteMachine.settingsLink')}>
            <IconButton
              onClick={() => { close(); navigate('/settings?tab=features&subtab=quote_machine'); }}
              aria-label={t('quoteMachine.settingsLink')}
              data-testid="quote-machine-settings-link"
              sx={{ mt: -0.5, color: 'text.secondary' }}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={close} aria-label={t('Close')} sx={{ mt: -0.5, mr: -1, color: 'text.secondary' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loadError && <Alert severity="error" sx={{ m: 3 }}>{loadError}</Alert>}
        {!meta && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}

        {meta && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' }, minHeight: '100%' }}>
            <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2.5, md: 3.25 }, display: 'flex', flexDirection: 'column', gap: { xs: 3.25, md: 3.75 }, minWidth: 0 }}>
              {!launchHidden && isQuoteMachineLaunchPeriod() && (
                <Alert
                  severity="info"
                  icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />}
                  onClose={hideLaunchInfo}
                  closeText={t('quoteMachine.launchHide')}
                  data-testid="quote-machine-launch-info"
                  sx={{ '& .MuiAlert-message': { width: '100%' } }}
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>{t('quoteMachine.launchTitle')}</AlertTitle>
                  <Box sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>{t('quoteMachine.launchBody')}</Box>
                  {canEditSettings && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SettingsOutlinedIcon />}
                      onClick={() => { close(); navigate('/settings?tab=features&subtab=quote_machine'); }}
                      data-testid="quote-machine-launch-settings"
                      sx={{ mt: 1.25 }}
                    >
                      {t('quoteMachine.launchSettings')}
                    </Button>
                  )}
                </Alert>
              )}
              {!meta.has_price_book && <Alert severity="warning">{t('quoteMachine.noPriceBook')}</Alert>}
              {vehicleStep}
              {jobsStep}
              {freeTextStep}
            </Box>

            <Box
              sx={(theme_) => ({
                bgcolor: 'background.default',
                [theme_.breakpoints.up('md')]: { borderLeft: `1px solid ${theme_.palette.divider}` },
                [theme_.breakpoints.down('md')]: { borderTop: `1px solid ${theme_.palette.divider}` },
              })}
            >
              <Box sx={{ position: { md: 'sticky' }, top: 0 }}>
                {summary}
                {picks.length > 0 && (
                  <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {groups.map(jobCard)}
                  </Box>
                )}
                {panelFooter}
                {fullScreen && (
                  <Box sx={{ px: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {customerPicker}
                    {createHint}
                    {workOrderButton}
                    {quoteButton}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3.5 }, py: { xs: 1.5, md: 1.75 }, gap: 1.5, flexWrap: 'wrap',
          borderTop: '1px solid', borderColor: 'divider',
          boxShadow: fullScreen ? '0 -6px 20px rgba(15, 35, 64, 0.08)' : 'none',
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        {fullScreen ? (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {overline(t('quoteMachine.inclVatShort'))}
              <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.5px', lineHeight: 1.1, color: totals ? 'text.primary' : 'text.disabled', ...numFont }}>
                {totals ? formatPriceWithCurrency(totals.gross) : '…'}
              </Typography>
            </Box>
            {sendButton}
          </>
        ) : (
          <>
            {/* The customer is optional and can be attached later: it sits next to where the
                quote is created, out of the way of the vehicle and the job menu. */}
            <Box sx={{ width: 'min(360px, 40%)', minWidth: 220 }}>{customerPicker}</Box>
            <Box sx={{ flex: 1, minWidth: 120, display: 'flex', justifyContent: 'flex-end' }}>{createHint}</Box>
            <Button onClick={close} sx={{ textTransform: 'none', color: 'text.secondary' }}>{t('Close')}</Button>
            {workOrderButton}
            {quoteButton}
            {sendButton}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default QuoteMachineDialog;
